import { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    Divider,
    CardContent,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Paper,
    Stack,
    MenuItem,
    InputAdornment
} from '@mui/material';
import {
    History,
    InfoOutlined,
    AccountBalanceWallet,
    AccountBalance,
    ArrowUpward,
    ArrowDownward,
    Search,
    AccessTimeFilled,
    ContentCopy
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVendorWallet, useVirtualAccount } from 'hooks/useWallet';
import { useRequestPayout, useAllVendorPayouts } from 'hooks/usePayouts';
import { useVendorTransactions } from 'hooks/useTransactions';
import { toast, ToastContainer } from 'react-toastify';

export default function WalletPage() {
    const navigate = useNavigate();

    // UI State
    const [openWithdraw, setOpenWithdraw] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');

    // Filter States
    const [filterType, setFilterType] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');

    // Data Hooks
    const { data: walletResponse, isLoading: isWalletLoading } = useVendorWallet();
    const { data: vAccount, isLoading: isVLoading } = useVirtualAccount();
    const { data: transactionResponse } = useVendorTransactions();
    const { data: payoutsResponse, isLoading: isPayoutsLoading } = useAllVendorPayouts({
        status: 'pending'
    });
    const { mutate: requestPayout, isLoading: isRequesting } = useRequestPayout();

    const transactions = transactionResponse?.data || [];

    // Data Mapping
    const wallet = walletResponse?.wallet;
    const bankDetails = walletResponse?.bankAccount;
    const pendingPayouts = payoutsResponse?.data || [];
    const handleWithdrawSubmit = () => {
        const amount = parseFloat(withdrawAmount);
        if (!amount || amount < 1000) return toast.error('Minimum withdrawal is ₦1,000');
        if (amount > wallet?.availableBalance) return toast.error('Insufficient available balance');

        requestPayout(
            {
                amount,
                bankName: bankDetails.bankName,
                accountNumber: bankDetails.accountNumber,
                accountName: bankDetails.accountName
            },
            {
                onSuccess: () => {
                    toast.success('Withdrawal request sent!');
                    setOpenWithdraw(false);
                    setWithdrawAmount('');
                },
                onError: (err) => toast.error(err?.response?.data?.message || 'Withdrawal failed')
            }
        );
    };

    const isLoading = isWalletLoading || isPayoutsLoading;

    return (
        <Box p={3} sx={{ background: '#f8f9fa', minHeight: '100vh' }}>
            <ToastContainer />

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" fontWeight={700}>
                    My Wallet
                </Typography>
                <Button variant="outlined" startIcon={<History />} onClick={() => navigate('/vendor/payouts')}>
                    Payout History
                </Button>
            </Stack>

            {/* BALANCE CARDS */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 4, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', right: -10, top: -10, opacity: 0.1 }}>
                            <AccountBalanceWallet sx={{ fontSize: 120 }} />
                        </Box>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                AVAILABLE BALANCE
                            </Typography>
                            {isLoading ? (
                                <Skeleton width="60%" height={60} />
                            ) : (
                                <Typography variant="h3" fontWeight={800} color="primary.main">
                                    ₦{wallet?.availableBalance?.toLocaleString() ?? 0}
                                </Typography>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<ArrowUpward />}
                                sx={{ mt: 3, borderRadius: 2, px: 4 }}
                                onClick={() => setOpenWithdraw(true)}
                                disabled={isLoading || wallet?.availableBalance <= 0}
                            >
                                Request Payout
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 4, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', bgcolor: '#fff9f2' }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                                PENDING REVENUE
                            </Typography>
                            {isLoading ? (
                                <Skeleton width="60%" height={60} />
                            ) : (
                                <Typography variant="h3" fontWeight={800} color="warning.main">
                                    ₦{wallet?.pendingBalance?.toLocaleString() ?? 0}
                                </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary" mt={2}>
                                Funds from orders in transit or escrow.
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        sx={{
                            borderRadius: 4,
                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                            background: 'linear-gradient(135deg, #673ab7 0%, #311b92 100%)',
                            color: 'white'
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="subtitle2" sx={{ opacity: 0.8 }} gutterBottom>
                                FUNDING ACCOUNT
                            </Typography>
                            {isVLoading ? (
                                <Skeleton width="80%" height={60} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                            ) : vAccount ? (
                                <>
                                    <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: 1 }}>
                                        {vAccount.accountNumber}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                        {vAccount.bankName}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1} mt={2}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="inherit"
                                            startIcon={<ContentCopy fontSize="small" />}
                                            sx={{ borderColor: 'rgba(255,255,255,0.3)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                                            onClick={() => {
                                                navigator.clipboard.writeText(vAccount.accountNumber);
                                                toast.success('Account number copied!');
                                            }}
                                        >
                                            Copy
                                        </Button>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                            Pay here to top-up instantly
                                        </Typography>
                                    </Stack>
                                </>
                            ) : (
                                <Typography variant="body2">Complete KYC to get funding account</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* PENDING PAYOUTS */}
            {pendingPayouts.length > 0 && (
                <Box mb={4}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                        <AccessTimeFilled color="warning" fontSize="small" />
                        <Typography variant="h6" fontWeight={700}>
                            Processing Payouts
                        </Typography>
                    </Stack>
                    <Stack spacing={1}>
                        {pendingPayouts.map((p) => (
                            <Paper
                                key={p._id}
                                sx={{
                                    p: 2.5,
                                    mb: 1.5,
                                    borderRadius: 3,
                                    borderLeft: '5px solid #ed6c02',
                                    bgcolor: '#fffdfa',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack spacing={0.5}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="h6" fontWeight={800} color="secondary.dark">
                                                ₦{p.amount.toLocaleString()}
                                            </Typography>
                                            <Chip
                                                label="Processing"
                                                size="small"
                                                color="warning"
                                                icon={<AccessTimeFilled style={{ fontSize: 14 }} />}
                                                sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem' }}
                                            />
                                        </Stack>

                                        <Box>
                                            <Typography variant="body2" fontWeight={600} color="text.primary">
                                                {p.accountName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {p.bankName} • {p.accountNumber}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Box textAlign="right">
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            Requested on
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700}>
                                            {new Date(p.createdAt).toLocaleDateString(undefined, {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(p.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </Typography>
                                    </Box>
                                </Stack>

                                {/* If the backend provides a "expected by" date or a note */}
                                <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Standard processing: 24-48 business hours. Reference: <b>{p._id.slice(-6).toUpperCase()}</b>
                                    </Typography>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* TABLE FILTERS */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center" justifyContent="space-between">
                <Typography variant="h6" fontWeight={700}>
                    Recent Activity
                </Typography>
                <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <TextField
                        size="small"
                        placeholder="Search ref..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                        sx={{ bgcolor: 'white', borderRadius: 1 }}
                    />
                    <TextField
                        select
                        size="small"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        sx={{ minWidth: 110, bgcolor: 'white' }}
                    >
                        <MenuItem value="ALL">All</MenuItem>
                        <MenuItem value="CREDIT">Credits</MenuItem>
                        <MenuItem value="DEBIT">Debits</MenuItem>
                    </TextField>
                </Stack>
            </Stack>

            {/* ACTIVITY TABLE */}
            <Card sx={{ borderRadius: 4, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: 'none' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f1f3f4' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                Array(5)
                                    .fill(0)
                                    .map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={5}>
                                                <Skeleton height={40} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                            ) : transactions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        No activity found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                transactions.map((tx) => (
                                    <TableRow key={tx._id} hover>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{tx.reference}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                {tx.type === 'CREDIT' ? (
                                                    <ArrowDownward fontSize="small" color="success" />
                                                ) : (
                                                    <ArrowUpward fontSize="small" color="error" />
                                                )}
                                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                    {tx.type.toLowerCase()}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {tx.type === 'CREDIT' ? '+' : '-'}₦{tx.amount.toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={tx.status}
                                                size="small"
                                                color={tx.status === 'successful' ? 'success' : 'default'}
                                                variant="light"
                                            />
                                        </TableCell>
                                        <TableCell color="text.secondary">{new Date(tx.createdAt).toLocaleDateString()}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* WITHDRAWAL DIALOG */}
            <Dialog open={openWithdraw} onClose={() => !isRequesting && setOpenWithdraw(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 800, fontSize: '1.4rem' }}>Request Payout</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                        Available: <b>₦{wallet?.availableBalance?.toLocaleString() ?? 0}</b>
                    </Alert>
                    <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={withdrawAmount}
                        onChange={(e) => setWithdrawAmount(e.target.value)}
                        sx={{ mb: 3 }}
                    />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={700}>
                        DISBURSEMENT ACCOUNT
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fcfcfc', borderRadius: 2, borderStyle: 'dashed' }}>
                        {bankDetails?.accountNumber ? (
                            <Stack direction="row" spacing={2} alignItems="center">
                                <AccountBalance color="action" />
                                <Box>
                                    <Typography variant="body2" fontWeight={700}>
                                        {bankDetails.accountName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {bankDetails.bankName} • {bankDetails.accountNumber}
                                    </Typography>
                                </Box>
                            </Stack>
                        ) : (
                            <Button size="small" onClick={() => navigate('/vendor/settings')}>
                                Update Bank Details
                            </Button>
                        )}
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenWithdraw(false)} disabled={isRequesting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleWithdrawSubmit}
                        disabled={isRequesting || !withdrawAmount || !bankDetails?.accountNumber}
                        fullWidth
                        size="large"
                    >
                        {isRequesting ? 'Processing...' : 'Confirm & Request'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
